<script setup lang="ts">
import TechnoFrame from "@/components/Common/TechnoFrame.vue";
import CardBackground from "@/components/RegistrationPage/Card/PcVersion/CardBackground.vue";
import CardInfo from "@/components/RegistrationPage/Card/PcVersion/CardInfo.vue";
</script>

<script lang="ts">
import {defineComponent, PropType} from 'vue'
import store from "@/store";
import {PassProperties} from "@/components/RegistrationPage/Card/PassProperties";

export default defineComponent({
    name: 'PassCard',

    data(): {
        tilt: {
            x: number
            y: number
        },

        targetTilt: {
            x: number
            y: number
        },

        scale: number

        interval: number | null
    } {
        return {
            tilt: {
                x: 0,
                y: 0,
            },

            targetTilt: {
                x: 0,
                y: 0,
            },

            scale: 1.24,
            interval: null,
        }
    },

    props: {
        tiltAngle: {
            type: Number,
            default: 20,
        },

        pass: {
            type: Object as PropType<PassProperties>,
            required: true,
        },
    },

    methods: {
        onMouseMove(event: MouseEvent): void
        {
            if (this.pageWidth <= store.state.mobileVersionThresholdWidth) return

            const box = this.$refs.box
            if (box instanceof HTMLDivElement) {
                this.targetTilt.x = this.tiltAngle * ( 2 * event.offsetX / box.clientWidth - 1)
                this.targetTilt.y = - this.tiltAngle * ( 2 * event.offsetY / box.clientHeight - 1)
            }
        },

        onMouseLeave(): void
        {
            if (this.pageWidth <= store.state.mobileVersionThresholdWidth) return

            this.targetTilt.x = 0
            this.targetTilt.y = 0
        },

        onClick(): void
        {
            store.state.selectedPass = this.pass
        },

        createInterval(): void
        {
            this.scale = 1
            if (this.interval) {
                this.tilt.x = 0
                this.tilt.y = 0

                clearInterval(this.interval)
            }

            if (this.pageWidth <= store.state.mobileVersionThresholdWidth) return

            this.scale = 1.25
            this.interval = setInterval(() => {
                const deltaX = Math.abs(this.targetTilt.x - this.tilt.x)
                if (deltaX < 0.001) {
                    this.tilt.x = this.targetTilt.x
                } else {
                    this.tilt.x += (this.targetTilt.x - this.tilt.x) / 50
                }

                const deltaY = Math.abs(this.targetTilt.y - this.tilt.y)
                if (deltaY < 0.001) {
                    this.tilt.y = this.targetTilt.y
                } else {
                    this.tilt.y += (this.targetTilt.y - this.tilt.y) / 50
                }
            })
        },
    },

    mounted(): void
    {
        this.createInterval()
    },

    computed: {
        pageWidth(): number
        {
            return store.state.pageSize.width
        },
    },

    watch: {
        pageWidth: {
            handler(newValue, oldValue): void
            {
                if (
                    (newValue <= store.state.mobileVersionThresholdWidth && oldValue > store.state.mobileVersionThresholdWidth)
                    || (newValue > store.state.mobileVersionThresholdWidth && oldValue <= store.state.mobileVersionThresholdWidth)
                ) {
                    this.createInterval()
                }
            },
        },
    },
})
</script>

<template>
    <div
        class="passCard"
        :class="{
            active: pass === store.state.selectedPass,
            mobile: store.state.pageSize.width <= store.state.mobileVersionThresholdWidth,
        }"
        @mousemove="onMouseMove"
        @mouseleave="onMouseLeave"
        @click="onClick"
        :ref="'box'"
        :style="{
            transform: `perspective(1000px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)`,
        }"
    >
        <TechnoFrame
            style="pointer-events: none"
            :offset="5"
            :size-modifier="0.3"
        />

        <img
            class="face"
            alt=""
            :src="`/img/page/landing/registration/avatar/${pass.avatar.image}/body.png`"
        >

        <img
            v-if="pass.secondaryBackground !== null"
            class="secondaryBackground"
            alt=""
            :src="`/img/page/landing/registration/misc/${pass.secondaryBackground}`"
        >

        <div class="content absolute-fill">
            <CardBackground
                :tilt="tilt"
                :tilt-angle="tiltAngle"
                :pass="pass"
                :bg-url="`${pass.background}.webp`"
            />

            <CardInfo
                :pass="pass"
            />
        </div>

        <img
            alt="Scouts Best Choice"
            class="medal"
            v-if="pass.isPlayersChoice"
            :src="`/img/page/landing/registration/misc/bestChoice.png`"
        />
    </div>
</template>

<style scoped lang="scss">
.passCard {
    display: flex;
    position: relative;
    min-width: 345px;
    max-width: 345px;
    min-height: 460px;
    max-height: 460px;
    border-radius: 10px 10px 25px 25px;
    transition:
        scale 0.075s ease,
        height 0.1s ease,
        box-shadow 0.2s ease,
        background-color 0.1s ease,
        //z-index 0.2s ease,
        translate 0.1s ease;
    z-index: 500;
    cursor: pointer;
    box-shadow: 0 0 21px 7px rgba(0, 0, 0, 1.0);

    .mobile {
        scale: 0.5;
    }

    * {
        pointer-events: none;
    }

    h4 {
        color: wheat;
        line-height: 1;
        text-shadow: 2px 2px 5px black;
        margin: 0;
    }

    &:hover {
        scale: v-bind('scale');
        //translate: 0 60px;
        //height: 500px;
        z-index: 1200;
        box-shadow: 0 0 70px 20px rgba(0, 0, 0, 1.0);
        background: rgba(15, 15, 15, 0.98);
    }

    &.active {
        z-index: 1100;
        box-shadow: v-bind('`0 0 19px 17px ${pass.glowColor}`');
    }

    .face {
        z-index: 500;
        position: absolute;
        width: 270px;
        bottom: 0;
        left: 0;
        border-bottom-left-radius: 20px;
    }

    .medal {
        position: absolute;
        bottom: -10px;
        right: -10px;
        width: 80px;
        height: 80px;
        border-radius: 666px;
        z-index: 9000;
        box-shadow: 0 0 10px 5px black;
    }

    .secondaryBackground {
        position: absolute;
        z-index: 501;
        width: 100%;
        height: 100%;
        border-radius: 10px;
        opacity: 0.7;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }
}

.content {
    overflow: hidden;
    border-radius: 10px 10px 25px 25px;
}
</style>
